import {
  Dispatch,
  PropsWithChildren, SetStateAction, createContext, useContext, useMemo,
} from 'react';
import { defaultTransitionStatus, TransitionStageContext, TransitionStatus } from '../../api/useAccountApi';
import {
  Account,
  Summary,
  defaultAccount,
  defaultSummary,
  useAccount,
} from '.';

export type AccountContextType = {
  id: string,
  account: Account,
  summary: Summary,
  creditSearchCompleted: boolean,
  transitionStatus: TransitionStatus,
  setAccountData: Dispatch<SetStateAction<Account>>,
  productsResultDigest: string | null,
  setProductsResultDigest: Dispatch<SetStateAction<string | null>>,
  fetchProductsSummary(accountId: string): Promise<void>,
  makeTransition: (stageContext?: TransitionStageContext) => Promise<void>,
  fetchTransitionStatus(): Promise<void>,
  updateAccount(field: string, value: any, disableIncrementUpdateCount?: boolean): Promise<void>,
  fetchAccount(incrementUpdateCount?: boolean): Promise<void>,
  appendToAccountArray<T>(field: string, value: T, initiator?: string): Promise<void>,
  deleteFromAccountArray(field: string, itemId: string): Promise<void>,
  verifyBankAccount(): Promise<void>;
}

const defaultAccountContext: AccountContextType = {
  id: '',
  account: defaultAccount,
  summary: defaultSummary,
  creditSearchCompleted: false,
  transitionStatus: defaultTransitionStatus,
  productsResultDigest: null,
  setProductsResultDigest: () => {},
  fetchProductsSummary: async () => {},
  setAccountData: () => {},
  makeTransition: async () => {},
  fetchTransitionStatus: async () => {},
  updateAccount: async () => {},
  fetchAccount: async () => {},
  appendToAccountArray: async () => {},
  deleteFromAccountArray: async () => {},
  verifyBankAccount: async () => {},
};

export const AccountContext = createContext<AccountContextType>(defaultAccountContext);

export function useAccountContext() {
  return useContext(AccountContext);
}

export function AccountProvider({ id, children }: PropsWithChildren<{id: string}>) {
  const account = useAccount(id);

  const contextValue = useMemo(() => ({ ...account, id }), [id, account]);

  return (
    <AccountContext.Provider value={contextValue}>{children}</AccountContext.Provider>
  );
}
